import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatPoints, temporadaVigente } from "@nhomi/core";
import { watchTemporadas, type Temporada } from "@/data/temporadas";
import { dayKey } from "@/data/plates";
import { Avatar } from "@/ui/primitives";

/* ============================================================
   Quem ganhou a semana passada

   Aparece no placar e no topo do feed durante a semana seguinte ao
   fechamento — campeão de duas semanas atrás já não é notícia. No feed
   dá para dispensar (fica dispensado só neste aparelho, por semana): é
   um anúncio, não uma tarefa.
   ============================================================ */

/** A temporada que ainda vale hoje (a da semana passada), ou null. */
export function useTemporadaVigente(cid: string): Temporada | null {
  const [temporadas, setTemporadas] = useState<Temporada[]>([]);
  useEffect(() => watchTemporadas(cid, setTemporadas, 2), [cid]);
  return temporadaVigente(temporadas, dayKey());
}

const chave = (cid: string, de: string) => `nhomi:campeao-visto:${cid}:${de}`;

function lerDispensado(cid: string, de: string): boolean {
  try { return localStorage.getItem(chave(cid, de)) === "1"; } catch { return false; }
}

export function CartaoDoCampeao(
  { cid, vigente, fotos, dispensavel = false }: {
    cid: string;
    vigente: Temporada | null;
    /** uid → foto, para o avatar do campeão. */
    fotos: Record<string, string | undefined>;
    dispensavel?: boolean;
  },
) {
  const [dispensado, setDispensado] = useState(false);
  useEffect(() => {
    setDispensado(!!vigente && dispensavel && lerDispensado(cid, vigente.de));
  }, [cid, vigente, dispensavel]);

  if (!vigente || vigente.campeoes.length === 0 || dispensado) return null;
  const campeoes = vigente.placar.filter((l) => vigente.campeoes.includes(l.uid));
  const primeiro = campeoes[0];
  if (!primeiro) return null;

  return (
    <div className={`campeao-wrap${dispensavel ? " no-feed" : ""}`}>
      <Link className="campeao-card" to={`/c/${cid}/temporada/${vigente.de}`}>
        <Avatar name={primeiro.name} photo={fotos[primeiro.uid]} size="md" coroa />
        <div className="t">
          <b>{campeoes.map((c) => c.name.split(" ")[0]).join(" e ")} {campeoes.length > 1 ? "ganharam" : "ganhou"} a semana passada</b>
          <small>{formatPoints(primeiro.pontos)} pts · {primeiro.dias} {primeiro.dias === 1 ? "dia ativo" : "dias ativos"} · ver o pódio</small>
        </div>
        <span className="trofeu" aria-hidden="true">🏆</span>
      </Link>
      {dispensavel && (
        <button className="campeao-fechar" aria-label="Dispensar"
          onClick={() => {
            try { localStorage.setItem(chave(cid, vigente.de), "1"); } catch { /* só não lembra */ }
            setDispensado(true);
          }}>×</button>
      )}
    </div>
  );
}
